import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { consoleCopy, shellCopy } from '@/copy'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Chip } from '@/ui/Chip/Chip'
import {
  RailButton,
  RailDivided,
  RailHeader,
  RailMore,
  RailNav,
  RailRow,
  RailSection,
} from '@/ui/Console/Console'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { Inline } from '@/ui/layout/Inline'
import { Text } from '@/ui/layout/Text'
import { Menu } from '@/ui/Menu/Menu'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { PresenceDot, StatusDot } from '@/ui/Status/Status'
import { messagesApi } from '../../api/messages'
import { isRouteActive, ROUTES } from '../../constants/routes'
import { useTopicQuery, useWorkspacesQuery } from '../../hooks/queries'
import { useAgentLiveStates } from '../../hooks/useAgentLiveState'
import { useTopicsQuery } from '../../hooks/useTopicsQuery'
import { useWorkspaceMembersQuery } from '../../hooks/useWorkspaceMembers'
import { parseStreamKey, useStreamStore } from '../../store/stream'
import { useWorkspaceStore } from '../../store/workspace'
import { parseReturnTo } from '../../utils/returnTo'
import { RailFooter } from './RailPrimitives'

/** People rows shown before the "show n more" collapse (design: 3). */
const PEOPLE_PREVIEW = 3

/**
 * Flat navigation rail (console redesign): rows only navigate — nothing
 * expands in place. Workspace header, waiting-on-you badge row, flat
 * TOPICS / AGENTS / PEOPLE sections, one workspace Resources link, and
 * the shared footer. Selection is URL-derived; there is no expansion
 * state (the old rail's persisted expansion store is not read).
 */
export function Rail({
  currentUser,
  placement = 'permanent',
}: {
  currentUser: string | null
  placement?: 'permanent' | 'drawer'
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { pathname, search } = useLocation()
  const { activeWorkspaceId, setActiveWorkspace, workspaces, setWorkspaces } =
    useWorkspaceStore()

  // Topic routes are authoritative for their workspace (same contract as
  // the old rail): while that read is in flight, workspace-scoped rows
  // act on nothing rather than briefly targeting the wrong workspace.
  const parsedTopicOrigin = parseReturnTo(`${pathname}${search}`, {
    allowedKinds: ['topic'],
  })
  const topicOrigin =
    parsedTopicOrigin?.kind === 'topic' ? parsedTopicOrigin : null
  const { data: originTopic } = useTopicQuery(topicOrigin?.topicId, {
    enabled: !!topicOrigin,
  })
  const contextualWorkspaceId = topicOrigin
    ? (originTopic?.workspace_id ?? undefined)
    : (activeWorkspaceId ?? undefined)

  // Reconcile the persisted active workspace against actual memberships
  // (a stale id would 403 the topics query).
  const { data: userWorkspaces = [] } = useWorkspacesQuery()
  useEffect(() => {
    if (userWorkspaces.length > 0) {
      setWorkspaces(userWorkspaces)
      const ids = userWorkspaces.map((o) => o.workspace_id)
      if (!activeWorkspaceId || !ids.includes(activeWorkspaceId)) {
        setActiveWorkspace(ids[0])
      }
    }
  }, [userWorkspaces, activeWorkspaceId, setActiveWorkspace, setWorkspaces])

  const { data: topics = [] } = useTopicsQuery(contextualWorkspaceId)
  // Shared agent-state derivation (also feeds the topic view's agent
  // header): agents list + waiting/turn-derived live states.
  const { agents, stateOf, waitingCount } = useAgentLiveStates(
    contextualWorkspaceId,
  )
  const { data: members = [] } = useWorkspaceMembersQuery(
    contextualWorkspaceId ?? '',
    { enabled: !!contextualWorkspaceId, staleTime: 60_000 },
  )

  const activeTurns = useStreamStore((s) => s.activeTurns)
  const liveUsers = useStreamStore((s) => s.liveUsers)

  const activeWorkspace = workspaces.find(
    (w) => w.workspace_id === contextualWorkspaceId,
  )
  const wsId = contextualWorkspaceId ?? ''
  const workspaceName =
    activeWorkspace?.name ?? shellCopy.rail.workspaceFallback
  const canManage =
    activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin'

  // ── Derived presence ────────────────────────────────────────────────────
  const topicIds = useMemo(
    () => new Set(topics.map((t) => t.topic_id)),
    [topics],
  )
  const turnsPerTopic = useMemo(() => {
    const counts = new Map<string, number>()
    for (const key in activeTurns) {
      const ids = activeTurns[key]
      if (!ids?.length) continue
      const { topicId } = parseStreamKey(key)
      if (!topicIds.has(topicId)) continue
      counts.set(topicId, (counts.get(topicId) ?? 0) + ids.length)
    }
    return counts
  }, [activeTurns, topicIds])

  const [showAllPeople, setShowAllPeople] = useState(false)
  const peopleShown = showAllPeople ? members : members.slice(0, PEOPLE_PREVIEW)
  const peopleHidden = members.length - peopleShown.length

  // ── View-selection state, derived from the current location ────────────
  // A topic row stays selected on its sub-routes (settings/resources) —
  // they are the same topic context; the flat rail has no sub-rows.
  const onTopic = (topicId: string) =>
    isRouteActive(pathname, ROUTES.topic(topicId))

  // Hover-warm the one query the topic view reads first (its thread list);
  // a failed warm-up is a no-op. Navigation itself is immediate — no
  // warm-up await, no sequence claims (the router last-write-wins).
  const warmTopic = (topicId: string) => {
    void queryClient
      .prefetchQuery({
        queryKey: ['threads', topicId, 'active'],
        queryFn: () => messagesApi.listThreads(topicId, 'active'),
        staleTime: 5_000,
      })
      .catch(() => undefined)
  }

  const skillsSel =
    !!wsId && isRouteActive(pathname, ROUTES.workspaceSkills(wsId))
  const recipesSel =
    !!wsId && isRouteActive(pathname, ROUTES.workspaceRecipes(wsId))
  const resSel =
    !!wsId && isRouteActive(pathname, ROUTES.workspaceResources(wsId))

  const onlineIds = liveUsers[contextualWorkspaceId ?? ''] ?? new Set<string>()

  return (
    <RailNav label={shellCopy.rail.label} placement={placement}>
      <RailHeader>
        <Menu
          placement="bottom-start"
          trigger={
            <RailButton
              aria-label={shellCopy.rail.switchWorkspace(workspaceName)}
            >
              <Inline gap="space-6" wrap={false}>
                <Avatar name={workspaceName} kind="workspace" />
                <Text weight="strong">{workspaceName}</Text>
                <Text tone="text-mute">
                  <Icon name="chevron-down" size="icon-embedded" />
                </Text>
              </Inline>
            </RailButton>
          }
          items={[
            ...workspaces.map((ws) => ({
              id: ws.workspace_id,
              label: ws.name,
              icon: <Avatar name={ws.name} kind="workspace" size="sm" />,
              onSelect: () => {
                setActiveWorkspace(ws.workspace_id)
                void navigate(ROUTES.home)
              },
            })),
            { type: 'separator' as const, id: 'sep' },
            {
              id: 'new',
              label: shellCopy.rail.newWorkspace,
              icon: 'plus',
              href: ROUTES.workspaceNew,
            },
          ]}
        />
        {wsId ? (
          <IconButton
            ground="well"
            label={shellCopy.rail.workspaceSettings}
            href={ROUTES.workspaceSettings(wsId)}
          >
            <Icon name="gear" />
          </IconButton>
        ) : (
          // No workspace resolved yet (a topic route's read in flight).
          <IconButton
            ground="well"
            label={shellCopy.rail.workspaceSettings}
            disabled
          >
            <Icon name="gear" />
          </IconButton>
        )}
      </RailHeader>

      {waitingCount > 0 && wsId && (
        <RailRow
          href={ROUTES.waiting(wsId)}
          current={pathname === ROUTES.waiting(wsId)}
          aria-label={shellCopy.rail.waitingLabel(waitingCount)}
          trailing={
            <Text variant="caption" numeric>
              {waitingCount}
            </Text>
          }
        >
          {shellCopy.rail.waiting}
        </RailRow>
      )}

      <RailSection
        title={shellCopy.rail.topics}
        action={
          wsId && (
            <IconButton
              ground="well"
              label={shellCopy.rail.newTopic}
              href={ROUTES.topicNew(wsId)}
            >
              <Icon name="plus" />
            </IconButton>
          )
        }
      >
        {topics
          .slice()
          .sort((a, b) => b.last_active_ms - a.last_active_ms)
          .map((t) => {
            const live = (turnsPerTopic.get(t.topic_id) ?? 0) > 0
            return (
              <RailRow
                key={t.topic_id}
                href={ROUTES.topic(t.topic_id)}
                current={onTopic(t.topic_id)}
                onMouseEnter={() => warmTopic(t.topic_id)}
                onFocus={() => warmTopic(t.topic_id)}
                trailing={
                  live && (
                    <Text variant="caption" tone="text-sub">
                      <StatusDot
                        tone={lifecycleTone('live')}
                        label={shellCopy.rail.live}
                      />
                    </Text>
                  )
                }
              >
                {t.name}
              </RailRow>
            )
          })}
      </RailSection>

      <RailSection
        title={shellCopy.rail.agents}
        action={
          wsId &&
          canManage && (
            <IconButton
              ground="well"
              label={shellCopy.rail.newAgent}
              href={ROUTES.agentNew(wsId)}
            >
              <Icon name="plus" />
            </IconButton>
          )
        }
      >
        {wsId &&
          agents.map((a) => {
            const state = stateOf(a.agent_id)
            return (
              <RailRow
                key={a.agent_id}
                href={ROUTES.agentProfile(wsId, a.agent_id)}
                current={isRouteActive(
                  pathname,
                  ROUTES.agentProfile(wsId, a.agent_id),
                )}
                trailing={
                  <Text variant="caption" tone="text-sub">
                    <StatusDot
                      tone={lifecycleTone(state)}
                      label={consoleCopy.agentState[state]}
                    />
                  </Text>
                }
              >
                {a.name}
              </RailRow>
            )
          })}
      </RailSection>

      <RailSection
        title={shellCopy.rail.people}
        action={
          wsId &&
          canManage && (
            <IconButton
              ground="well"
              label={shellCopy.rail.invitePerson}
              href={ROUTES.workspaceSettingsTab(wsId, 'members')}
            >
              <Icon name="plus" />
            </IconButton>
          )
        }
      >
        {wsId &&
          peopleShown.map((m) => {
            const self = m.username === currentUser
            const online = onlineIds.has(m.user_id)
            return (
              <RailRow
                key={m.user_id}
                href={ROUTES.personProfile(wsId, m.username)}
                current={isRouteActive(
                  pathname,
                  ROUTES.personProfile(wsId, m.username),
                )}
                trailing={
                  <>
                    {self && <Chip variant="kind" label={shellCopy.rail.you} />}
                    <PresenceDot
                      state={online ? 'online' : 'idle'}
                      label={
                        online ? shellCopy.rail.online : shellCopy.rail.offline
                      }
                      hideLabel
                    />
                  </>
                }
              >
                {m.username}
              </RailRow>
            )
          })}
        {peopleHidden > 0 && (
          <RailMore onClick={() => setShowAllPeople(true)}>
            {shellCopy.rail.showMore(peopleHidden)}
          </RailMore>
        )}
      </RailSection>

      <RailDivided push>
        {wsId && (
          <>
            <RailRow
              href={ROUTES.workspaceSkills(wsId)}
              current={skillsSel}
              action={
                canManage && (
                  <IconButton
                    ground="well"
                    label={shellCopy.rail.newSkill}
                    href={`${ROUTES.workspaceSkills(wsId)}?create=true`}
                  >
                    <Icon name="plus" />
                  </IconButton>
                )
              }
            >
              {shellCopy.rail.skills}
            </RailRow>
            <RailRow href={ROUTES.workspaceRecipes(wsId)} current={recipesSel}>
              {shellCopy.rail.recipes}
            </RailRow>
            <RailRow
              href={ROUTES.workspaceResources(wsId)}
              current={resSel}
              action={
                <IconButton
                  ground="well"
                  label={shellCopy.rail.newResource}
                  href={ROUTES.resourceNew(wsId)}
                  state={{ returnTo: ROUTES.workspaceResources(wsId) }}
                >
                  <Icon name="plus" />
                </IconButton>
              }
            >
              {shellCopy.rail.resources}
            </RailRow>
          </>
        )}
      </RailDivided>

      <RailFooter currentUser={currentUser} />
    </RailNav>
  )
}
